// @flow
import React, { Component } from "react";
import { noop, bindAll, find, findIndex, isEqual } from "lodash";
import { type BetType } from "@tvg/types/Bet";
import type { BinaryFn, UnaryFn, NullaryFn } from "@tvg/types/Functional";
import type { WagerType, WagerTypeCodesEnum } from "@tvg/types/Wager";
import {
  WagerTypeContainer,
  SelectorWrapper,
  SubSelectorWrapper,
  SubSelectorList,
  WagerTypeButton,
  OptionsWrapper,
  BackButton,
  TextContainer
} from "./styled-components";
import Icon from "../../_static/Icons";
import { arrowBack } from "../../_static/Icons/icons";

import buildColor from "../../_static/ColorPalette";
import ScrollWrapper from "../../_molecule/ScrollWrapper";

type Props = {
  /**
   * Array of wager types to display on the selector
   */
  wagerTypes: Array<BetType>,
  /**
   * Active WagerTypeCode that is selected
   */
  active: string,
  /**
   * Active Race that is selected on a Leg bet
   */
  activeRace: number,
  /**
   * Callback to run when selecting a wager type
   */
  onSelectWagerType: BinaryFn<WagerType, ?WagerType, void>,
  /**
   * Callback to run when selecting a sub wager type
   */
  onSelectLegRace: UnaryFn<number, mixed>,
  /**
   * Callback when button back is clicked on wager type selector.
   */
  onBackToMainWagerTypeSelector: NullaryFn<mixed>,
  /**
   * Show Main wager type
   */
  isMainWagerType: boolean,
  /**
   * Current race number
   */
  raceNumber: number,
  /**
   * If wager selector is fixed - value of top
   */
  fixedOffset: number,
  /**
   * The device this is displaying on
   */
  device: "mobile" | "tablet"
};

type StateWagerType = {
  id: number,
  code: WagerTypeCodesEnum,
  name: string,
  legCount: number,
  isKey: boolean,
  isWheel: boolean,
  isBox: boolean,
  options: WagerType[]
};

type State = {
  active: string,
  activeRace: number,
  isMainWagerType: boolean,
  wagerTypes: StateWagerType[]
};

const getWagerTypeByModifier = (
  wagerTypes: StateWagerType[],
  active: string
): ?StateWagerType => {
  let found = false;
  let i = -1;
  let indexSubWT = -1;

  do {
    i += 1;
    indexSubWT = findIndex(
      wagerTypes[i] ? wagerTypes[i].options : [],
      (wt) => wt.code === active
    );
    found = indexSubWT >= 0;
  } while (!found && i < wagerTypes.length);

  return found ? wagerTypes[i] : undefined;
};

export const getModifierName = (
  name: string,
  code: string,
  index: number
): string => {
  if (index === 0) {
    return code;
  }
  if (name.includes("Wheel")) {
    return "WHEEL";
  }
  if (name.includes("Key Box")) {
    return "KEYBOX";
  }
  if (name.includes("Box")) {
    return "BOX";
  }
  if (name.includes("Key")) {
    return "KEY";
  }

  return name;
};

const getWagerTypesList = (wagerTypes: BetType[]) => {
  const wagerTypesList = [];

  wagerTypes.forEach((wt) => {
    const index = findIndex(
      wagerTypesList,
      (group) => group.code === wt.group.code
    );

    if (index < 0) {
      wagerTypesList.push({
        id: wt.group.id,
        code: wt.group.code,
        name: wt.group.name,
        legCount: wt.legCount,
        isKey: wt.isKey,
        isWheel: wt.isWheel,
        isBox: wt.isBox,
        options: [wt.type]
      });
    } else {
      wagerTypesList[index].options.push(wt.type);
    }
  });

  return wagerTypesList;
};

const getIsMainWagerType = (
  wagerTypes: StateWagerType[],
  active: string,
  isMainWagerType: boolean
): boolean => {
  if (!isMainWagerType) {
    const wt = getWagerTypeByModifier(wagerTypes, active);

    return wt ? wt.options.length < 1 : true;
  }

  return true;
};

export default class WagerTypeSelector extends Component<Props, State> {
  static defaultProps = {
    wagerTypes: [],
    onSelectWagerType: noop,
    onSelectLegRace: noop,
    onBackToMainWagerTypeSelector: noop,
    isMainWagerType: true,
    raceNumber: 1,
    fixedOffset: 48,
    activeRace: 0,
    active: "",
    device: "mobile"
  };

  constructor(props: Props) {
    super(props);
    this.state = {
      active:
        this.props.active ||
        (this.props.wagerTypes.length > 0 &&
          this.props.wagerTypes[0].type.code) ||
        "",
      activeRace: this.props.activeRace || 0,
      isMainWagerType: this.props.isMainWagerType,
      wagerTypes: getWagerTypesList(this.props.wagerTypes)
    };

    this.ref = React.createRef();

    bindAll(this, [
      "handleSelectWagerType",
      "handleSelectModifier",
      "handleSelectRace",
      "renderModifiersList",
      "renderLegRacesList",
      "renderWagerTypeList"
    ]);
  }

  componentWillReceiveProps(nextProps: Props) {
    if (
      this.state.active !== nextProps.active ||
      this.state.activeRace !== nextProps.activeRace ||
      this.state.isMainWagerType !== nextProps.isMainWagerType ||
      !isEqual(this.props.wagerTypes, nextProps.wagerTypes)
    ) {
      this.setState({
        active: nextProps.active,
        activeRace: nextProps.activeRace,
        isMainWagerType: getIsMainWagerType(
          this.state.wagerTypes,
          nextProps.active || this.state.active,
          nextProps.isMainWagerType
        ),
        wagerTypes: getWagerTypesList(nextProps.wagerTypes)
      });
    }
  }

  shouldComponentUpdate(nextProps: Props, nextState: State) {
    return (
      this.state.active !== nextState.active ||
      this.state.active !== nextProps.active ||
      this.props.wagerTypes.length !== nextProps.wagerTypes.length ||
      this.state.activeRace !== nextProps.activeRace ||
      this.state.activeRace !== nextState.activeRace ||
      this.props.isMainWagerType !== nextProps.isMainWagerType ||
      this.props.fixedOffset !== nextProps.fixedOffset ||
      this.state.isMainWagerType !== nextProps.isMainWagerType
    );
  }

  // $FlowBug
  ref: Element<*>;

  handleSelectWagerType(wagerType: StateWagerType) {
    this.setState({
      active: wagerType.code
    });
    this.props.onSelectWagerType({
      id: wagerType.id,
      code: wagerType.code,
      name: wagerType.name
    });

    this.ref.scrollTo(0, 0);
  }

  handleSelectModifier(betType: StateWagerType, wagerType: WagerType) {
    this.setState({ active: wagerType.code });
    this.props.onSelectWagerType(wagerType, {
      id: betType.id,
      code: betType.code,
      name: betType.name
    });
  }

  handleSelectRace(id: number) {
    this.setState({ activeRace: id });
    this.props.onSelectLegRace(id);
  }

  renderModifiersList(wagerType: StateWagerType) {
    return (
      <SubSelectorWrapper isVisible={!this.state.isMainWagerType}>
        <ScrollWrapper>
          <SubSelectorList ref={(comp) => (this.ref = comp)}>
            {wagerType.options.map((opt, index) => (
              <li key={`modifier-${opt.code}`}>
                <WagerTypeButton
                  onClick={() => this.handleSelectModifier(wagerType, opt)}
                  selected={this.state.active === opt.code}
                  qaLabel={`wagerTypeSelector-wagerTypeBtn-${opt.code}`}
                >
                  {getModifierName(opt.name, opt.code, index)}
                </WagerTypeButton>
              </li>
            ))}
          </SubSelectorList>
        </ScrollWrapper>
      </SubSelectorWrapper>
    );
  }

  renderLegRacesList(legCount: number, raceNumber: number) {
    const legRacesList = [];

    for (let i = 0; i < legCount; i += 1) {
      legRacesList.push(
        <li key={`wagerType-R${raceNumber + i}`}>
          <WagerTypeButton
            onClick={() => this.handleSelectRace(i)}
            selected={this.state.activeRace === i}
            qaLabel="wagerTypeSelector-wagerTypeBtn-race"
          >
            {`R${raceNumber + i}`}
          </WagerTypeButton>
        </li>
      );
    }

    return (
      <SubSelectorWrapper isVisible={!this.state.isMainWagerType}>
        <ScrollWrapper>
          <SubSelectorList ref={(comp) => (this.ref = comp)}>
            {legRacesList}
          </SubSelectorList>
        </ScrollWrapper>
      </SubSelectorWrapper>
    );
  }

  renderSubWagerTypeList() {
    let subWagerTypes;
    let wagerType = find(
      this.state.wagerTypes,
      (wt) => wt.code === this.state.active
    );

    if (wagerType) {
      subWagerTypes =
        wagerType.legCount > 1
          ? this.renderLegRacesList(wagerType.legCount, this.props.raceNumber)
          : this.renderModifiersList(wagerType);
    } else {
      wagerType = getWagerTypeByModifier(
        this.state.wagerTypes,
        this.state.active
      );
      subWagerTypes = wagerType ? (
        this.renderModifiersList(wagerType)
      ) : (
        <SubSelectorWrapper>
          <ScrollWrapper>
            <SubSelectorList ref={(comp) => (this.ref = comp)} />
          </ScrollWrapper>
        </SubSelectorWrapper>
      );
    }

    return (
      <OptionsWrapper isVisible={!this.state.isMainWagerType}>
        <BackButton
          isVisible={!this.state.isMainWagerType}
          qaLabel="wagerTypeSelector-backBtn"
          onClick={this.props.onBackToMainWagerTypeSelector}
        >
          <Icon size={16} icon={arrowBack} color={buildColor("grey", "900")} />
          <TextContainer>
            <span>BET TYPE</span>
            <span data-qa-label="wagerTypeSelector-backBtn-wagerType">
              {wagerType && wagerType.name}
            </span>
          </TextContainer>
        </BackButton>
        {subWagerTypes}
      </OptionsWrapper>
    );
  }

  renderWagerTypeList() {
    return (
      <ScrollWrapper showFaders={this.state.isMainWagerType}>
        <SelectorWrapper
          isVisible={this.state.isMainWagerType}
          device={this.props.device}
        >
          {this.state.wagerTypes.map((wagerType: StateWagerType) => (
            <li key={`wagerType-${wagerType.code}`}>
              <WagerTypeButton
                onClick={() => this.handleSelectWagerType(wagerType)}
                qaLabel={`wagerTypeSelector-wagerTypeBtn-${wagerType.name.replace(
                  " ",
                  ""
                )}`}
              >
                {wagerType.name}
              </WagerTypeButton>
            </li>
          ))}
        </SelectorWrapper>
      </ScrollWrapper>
    );
  }

  render() {
    return (
      this.props.wagerTypes.length > 0 && (
        <WagerTypeContainer fixedOffset={this.props.fixedOffset}>
          {this.renderWagerTypeList()}
          {this.renderSubWagerTypeList()}
        </WagerTypeContainer>
      )
    );
  }
}
